<template>
    <lateral-content
        v-model="innerShowParametrizacao"
        :isLoading="isLoading"
        confirmClose
        header
        :title="$t('parametrizacoes-campos')"
    >
        <loading :isLoading="isLoading" block />
        <alert-container :messages="msgs" />
        <template v-slot:header-right>
            <b-button
                icon-right="delete-outline"
                @click="onReset()"
                :disabled="isLoading || parametrizacaoList.length == 0"
                class="mr-1"
                :title="$t('resetar')"
                v-tippy
            />
            <b-button
                icon-right="checkbox-multiple-blank-outline"
                @click="changeSelect(false)"
                :disabled="isLoading || parametrizacaoList.length == 0"
                class="mr-1"
                :title="$t('desselecionar-todos')"
                v-tippy
            />
            <b-button
                icon-right="checkbox-multiple-marked-outline"
                @click="changeSelect(true)"
                :disabled="isLoading || parametrizacaoList.length == 0"
                class="mr-4"
                :title="$t('selecionar-todos')"
                v-tippy
            />
            <b-button
                type="is-warning is-silver"
                icon-right="close"
                @click="onFecharModal()"
                :disabled="isLoading"
                class="mr-2"
                :title="$t('fechar')"
                v-tippy
            />
            <b-button
                type="is-primary"
                icon-left="content-save-outline"
                @click="manageBulk()"
                :disabled="isLoading || parametrizacaoList.length == 0"
            >
                {{ $t('salvar') }}
            </b-button>
        </template>
        <div class="px-3 content-geral-campos">
            <empty-table-message v-if="parametrizacaoList.length == 0" class="isEmpty" />
            <div v-else class="campos">
                <v-draggable :list="parametrizacaoList">
                    <div v-for="param in parametrizacaoList" :key="param.id">
                        <div class="page-container form-columns-fit">
                            <div class="columns">
                                <span
                                    class="gp-handle"
                                    v-tippy 
                                    :title="$t('msg-campo-ordencao')"
                                >
                                    <svg class="svg-icons">
                                        <use
                                            :xlink:href="
                                                require('@/assets/img/icons/drag.svg') + '#layer'
                                            "
                                        ></use>
                                    </svg>
                                </span>
                                <div>
                                    <b-checkbox v-model="param.sisAssTelaCampoUsuario.situacao" />
                                    {{ $t(param.nome) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </v-draggable>
            </div>
        </div>
    </lateral-content>
</template>

<script>
/* 
como usar:
    <Parametrizacao
        :showParametrizacao="showParametrizacao"
        @fecharModal="showParametrizacao = false"
        @table_fields="table_fields = $event"
    />

Página onde foi implementado:
    src\components\vendas\atendimento\AtendimentoList.vue
Chamado 
    #90309
Descrição
    Na tabela app_tela_campo de cadastro dos campos da tela, tem três novos campos:
    bloqueado: Campo que não se pode desselecionar e não aparece na listagem de parametrização;
    align: Campo bloqueado que deve aparecer antes ou depois dos campos parametrizáveis;
    sortable: que pode ser ordenado na tabela;
    A tabela sis_ass_tela_campo_usuario tem o campo ORDEM 
    para sobrepor a ordem da tabela app_tela_campo segundo parametrização do usuário.
*/
export default {
    name: 'ParametrizacaoCampoUsuario',
    props: {
        // controla a visibilidade do modal pelo pai
        showParametrizacao: {
            type: Boolean,
            default: false,
        },
        codigounicotela: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            // lista interna de campos
            parametrizacaoList: [],
            isLoading: false,
            msgs: [],
            isChanged: false,
            innerShowParametrizacao: false,
            // lista de campos completa
            app_tela_campo: [],
            // lista de campos para a tabela do pai
            table_fields: [],
        };
    },
    created() {
        this.carregarCampos();
    },
    watch: {
        parametrizacaoList: {
            deep: true,
            handler: function () {
                this.isChanged = !this.isLoading;
            },
        },
        showParametrizacao: function(newValue) {
            this.innerShowParametrizacao = newValue;
        }
    },
    methods: {
        carregarCampos() {
            this.isLoading = true;
            this.app_tela_campo = [];
            this.parametrizacaoList = [];
            this.table_fields = [];

            this.$axios
                .get(
                    `controle/app/app_tela_campo/all/bytela/${this.codigounicotela}`
                )
                .then((response) => {
                    this.app_tela_campo = response.data.content;
                })
                .catch(() => {
                    this.$root.showMessage('error', 'msg-erro-dados-geral');
                    this.$emit('table_fields', []);
                })
                .finally((_) => {
                    if (this.app_tela_campo.length > 0) {
                        this.fillDataCampos();
                    } else {
                        this.isLoading = false;
                        this.isChanged = false;
                        this.$emit('table_fields', []);
                    }
                });
        },
        fillDataCampos() {
            // primeiros campos
            let temp1 = this.app_tela_campo.filter((f) => f.bloqueado == 1 && f.align == 0);
            // campos personalizaveis
            let temp2 = this.app_tela_campo.filter(
                (f) => f.bloqueado == 0 && f.sisAssTelaCampoUsuario.situacao
            );

            // ultimos campos
            let temp3 = this.app_tela_campo.filter((f) => f.bloqueado == 1 && f.align == 1);

            // ordena por ordem por campos bloqueados e por campos editávais
            temp1 = this.sortArrayByObjectKey(temp1, 'ordem');
            temp2 = this.sortArrayByObjectKey(temp2, 'ordem');
            temp3 = this.sortArrayByObjectKey(temp3, 'ordem');

            let tempFinal = [...temp1, ...temp2, ...temp3];

            this.table_fields = tempFinal.map((field) => {
                return {
                    key: field.nome,
                    label: field.nome,
                    sortable: field.sortable,
                };
            });

            // envia para o pai os campos da tabela
            this.$emit('table_fields', this.table_fields);

            this.parametrizacaoList = this.arrayToStringAndBack(
                this.app_tela_campo.filter((f) => f.bloqueado == 0)
            );
            this.parametrizacaoList = this.sortArrayByObjectKey(this.parametrizacaoList, 'ordem');

            setTimeout(() => {
                this.isLoading = false;
                this.isChanged = false;
            }, 500);
        },
        manageBulk() {
            // salva nova ordenação dos campos personalizáveis
            this.isLoading = true;
            // mosta o objeto para a tabela sis_ass_tela_campo_usuario
            let sisAssTelaCampoUsuarioList = this.parametrizacaoList.map((item, index) => {
                return {
                    id: item.sisAssTelaCampoUsuario.id,
                    id_usuario: item.sisAssTelaCampoUsuario.id_usuario,
                    id_app_tela_campo: item.id,
                    situacao: item.sisAssTelaCampoUsuario.situacao,
                    ordem: index + 1,
                };
            });

            this.$axios
                .post(`geral/sis_ass_tela_campo_usuario/ass`, sisAssTelaCampoUsuarioList)
                .then((response) => {
                    if (response.data.status === 500) {
                        this.$root.showMessage('error', response.data.message, true);
                    } else {
                        this.$root.showMessage('success', 'msg-sucesso-ao-adicionar-campo');
                    }
                })
                .catch(() => {
                    this.$root.showMessage('alert', 'msg-erro-salvar', true);
                })
                .finally((_) => {
                    this.isLoading = false;
                    // recarrega os campos para pegar os novos id's e refazer a listagem do pai
                    this.carregarCampos();
                });
        },

        onFecharModal() {
            if (this.isChanged) {
                this.$root.confirm({
                    message: this.$t('msg-tem-certeza-alteracoes'),
                    onConfirm: () => this.$emit('fecharModal', true),
                });
            } else {
                this.$emit('fecharModal', true);
            }
        },
        changeSelect(value) {
            this.isLoading = true;
            this.parametrizacaoList.forEach((item) => {
                item.sisAssTelaCampoUsuario.situacao = value;
            });
            this.isLoading = false;
        },
        onReset() {
            this.$root.confirm({
                message: this.$t('msg-tem-certeza-alteracoes'),
                onConfirm: () => this.reset(),
            });
        },
        reset() {
            // reseta ordenação dos campos personalizáveis
            this.isLoading = true;

            this.$axios
                .post(`geral/sis_ass_tela_campo_usuario/reset`, this.app_tela_campo)
                .then((response) => {
                    if (response.data.status === 500) {
                        this.$root.showMessage('error', response.data.message, true);
                    } else {
                        this.$root.showMessage('success', 'msg-sucesso-salvar');
                    }
                })
                .catch(() => {
                    this.$root.showMessage('alert', 'msg-erro-salvar', true);
                })
                .finally((_) => {
                    this.isLoading = false;
                    // recarrega os campos para pegar os novos id's e refazer a listagem do pai
                    this.carregarCampos();
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.content-geral-campos {
    height: 100%;
    .isEmpty {
        position: relative;
        display: flex;
        flex-flow: column;
        height: 100%;
    }

    .campos {
        height: auto;
        width: 100%;
        padding-top: 10px;
        .page-container {
            margin-bottom: 10px;
            padding-bottom: 10px;
            border: solid 2px #ffff;

            &:hover,
            &:focus {
                border: 2px solid $primary-color-hover;
            }
        }
        .gp-handle {
            display: flex;
            align-items: center;
            margin-right: 10px;

            svg {
                transform: rotate(90deg);
            }
        }
    }
}
</style>
